import * as React from 'react'
import {Debouncer} from '../../common/debouncer'

export interface DragControl<Id> {
  draggedId?: Id
  dragOverId?: Id
  dragStart(id: Id): (e: React.DragEvent) => void
  dragEnd(e: React.DragEvent): void
  drop(to: Id): (e: React.DragEvent<HTMLElement>) => void
  dragEnter(inside: Id): (e: React.DragEvent) => void
  dragLeave(inside: Id): (e: React.DragEvent) => void
  dragOver(inside: Id, cursor?: DragCursor): (e: React.DragEvent) => void
}

export interface DragProps {
  onDragStart: React.DragEventHandler
  onDragOver: React.DragEventHandler
  onDrop: React.DragEventHandler
  onDragLeave: React.DragEventHandler
  onDragEnter: React.DragEventHandler
  onDragEnd: React.DragEventHandler
}

// when set cursor='none', animation appears
export type DragCursor = 'move' | undefined

export const dragPropsFromCtl = <T>(
  id: T,
  dragCtl: DragControl<T>,
  cursor?: DragCursor
): DragProps => {
  return {
    onDragStart: dragCtl.dragStart(id),
    onDragOver: dragCtl.dragOver(id, cursor),
    onDrop: dragCtl.drop(id),
    onDragLeave: dragCtl.dragLeave(id),
    onDragEnter: dragCtl.dragEnter(id),
    onDragEnd: dragCtl.dragEnd
  }
}

let dragOverFrame: number
const leaveDebouncer = new Debouncer(100)

interface UseDraggableProps<T> {
  onDrop?: (id: T, over: T) => void
  onDragStart?: (id: T, e: React.DragEvent) => void
  onDragEnd?: (id: T) => void
  onDragOver?: (draggedId: T, overId: T, e?: React.DragEvent) => void
  ghost?: HTMLElement | ((id: T) => HTMLElement)
}

// eslint-disable-next-line max-lines-per-function
export const useDraggable = <T>({
  onDrop,
  onDragStart,
  onDragEnd,
  onDragOver,
  ghost
}: UseDraggableProps<T>): DragControl<T> => {
  const [draggedId, setDraggedId] = React.useState<T>(undefined)
  const [dragOverId, setDragOverId] = React.useState<T>(undefined)

  const requestDragOverId = (
    id: T,
    toRemove = false,
    e?: React.DragEvent
  ): void => {
    cancelAnimationFrame(dragOverFrame)
    dragOverFrame = requestAnimationFrame(() => {
      if (toRemove) {
        dragOverId === id && setDragOverId(undefined)
      } else {
        leaveDebouncer.clear()
        e && onDragOver?.(draggedId, id, e)
        dragOverId !== id && setDragOverId(id)
      }
    })
  }

  return {
    draggedId,
    dragOverId,
    dragStart: id => e => {
      e.stopPropagation()
      if (ghost) {
        const ghostElement = typeof ghost === 'function' ? ghost(id) : ghost
        e.dataTransfer.setDragImage(ghostElement, 16, 16)
      }
      onDragStart?.(id, e)
      // use setTimeout to workaround chrome issue with dom updates for drag
      setTimeout(() => setDraggedId(id))
    },
    dragEnd: e => {
      e.stopPropagation()
      e.preventDefault()
      onDragEnd?.(draggedId)
      setDraggedId(undefined)
      requestDragOverId(undefined)
    },
    drop: (to: T) => (e: React.DragEvent<HTMLElement>) => {
      e.stopPropagation()
      e.preventDefault()
      onDrop?.(draggedId, to)
    },
    dragEnter: inside => e => {
      e.stopPropagation()
      e.preventDefault()
      requestDragOverId(inside)
    },
    dragOver: (inside, cursor) => e => {
      e.preventDefault()
      e.stopPropagation()
      if (e?.dataTransfer) {
        e.dataTransfer.dropEffect = cursor
      }

      requestDragOverId(inside, false, e)
    },
    dragLeave: inside => e => {
      e.stopPropagation()
      leaveDebouncer.runButNotOften(() => requestDragOverId(inside, true))
    }
  }
}
